import { useState, useMemo, useCallback } from 'react';
import { InvoicesDashboard } from '@/app/features/DebtorPortal/Dashboard/dashboard.types';
import { formatDateForSubmission } from '@/app/shared/utils/tableUtils';
import { RowChanges } from '../../invoiceUtils/invoiceTypes';
import {
  mergeRowsWithChanges,
  buildSubmitPayload,
  getInvoiceIdsWithChanges,
  getModifiedRows,
  validateRows,
  validateModifiedRows,
  getInvoiceIdsWithWarnings,
  isInvoicePastDue,
  buildBulkPaymentPayload,
  buildBulkWillNotPayPayload,
} from '../../invoiceUtils/invoiceTransformers';
import { updateDebtorFeedback } from '@/app/shared/api/endpoints/updateDebtorFeedback';

export interface UseInvoicesPendingChangesReturn {
  // Merged rows with changes
  rows: InvoicesDashboard[];

  // Pending changes state
  pendingChanges: Map<number, RowChanges>;
  modifiedRows: InvoicesDashboard[];
  hasChangesToSubmit: boolean;
  invoiceIdsWithChanges: Set<number>;

  // Row update handlers
  handlePaymentDateChange: (
    invoiceNumber: string,
    selectedPaymentDate: string | undefined,
  ) => void;
  handleWillNotPayChange: (
    invoiceNumber: string,
    disputedReasonId: string,
    disputedReasonDesc: string,
    note: string,
  ) => void;
  updateRowWithModalData: (
    invoiceNumber: string,
    value: string,
    disputedReasonId?: string,
    disputedReasonDesc?: string,
    ) => void;

 // Change management

  handleBulkPaymentDateChangeAndSubmit: (
    invoiceNumbers: string[],
    selectedPaymentDate: string,
    note?: string,
  ) => Promise<boolean>;
  handleBulkWillNotPayChangeAndSubmit: (
    invoiceNumbers: string[],
    disputedReasonId: string,
    disputedReasonDesc: string,
    note: string,
  ) => Promise<boolean>;
  resetRowChanges: (invoiceId: number) => void;
  resetAllChanges: () => void;
  rowHasChanges: (invoiceId: number) => boolean;

  // Submission
  handleSubmitUpdates: () => Promise<boolean>;
  isSubmitting: boolean;
  submitError: string | null;

  // Validation - for all rows (display purposes)
  isFormValid: boolean;
  hasRowsWithWarnings: boolean;
  rowsWithWarningsCount: number;
  invoiceIdsWithWarnings: Set<number>;
  rowHasWarning: (invoiceId: number) => boolean;
  isRowPastDue: (invoiceId: number) => boolean;

  // Validation - for modified rows only (submission purposes)
  hasInvalidModifiedRows: boolean;
  invalidModifiedRowsCount: number;
}

export const useInvoicesPendingChanges = (
  rawRows: InvoicesDashboard[],
  onSubmitSuccess?: () => void,
): UseInvoicesPendingChangesReturn => {
  // Pending changes storage - keyed by invoiceId for persistence across pages
  const [pendingChanges, setPendingChanges] = useState<Map<number, RowChanges>>(
    new Map(),
  );

  // Submit state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Merge raw rows with pending changes to create display rows
  const rows = useMemo(
    () => mergeRowsWithChanges(rawRows, pendingChanges),
    [rawRows, pendingChanges],
  );

  // Validation state for all rows (display purposes)
  const allRowsValidationState = useMemo(() => validateRows(rows), [rows]);

  // Get invoice IDs with warnings (all rows)
  const invoiceIdsWithWarnings = useMemo(
    () => getInvoiceIdsWithWarnings(rows),
    [rows],
  );

  // Pass rawRows to compare changes against original data
  const modifiedRows = useMemo(
    () => getModifiedRows(pendingChanges, rawRows),
    [pendingChanges, rawRows],
  );

  // Validation state for modified rows only (submission purposes)
  const modifiedRowsValidationState = useMemo(
    () => validateModifiedRows(modifiedRows, rawRows),
    [modifiedRows, rawRows],
  );

  // Check based on actual modified rows count
  const hasChangesToSubmit = useMemo(
    () => modifiedRows.length > 0,
    [modifiedRows],
  );

  // Get invoice IDs with pending changes
  const invoiceIdsWithChanges = useMemo(
    () => getInvoiceIdsWithChanges(pendingChanges, rawRows),
    [pendingChanges, rawRows],
  );

  // Check if form is valid for submission (has changes AND no invalid modified rows)
  const isFormValid = useMemo(() => {
    if (!hasChangesToSubmit) {
      return false;
    }
    return modifiedRowsValidationState.isValid;
  }, [hasChangesToSubmit, modifiedRowsValidationState.isValid]);

  // Payment date change handler
  const handlePaymentDateChange = useCallback(
    (invoiceNumber: string, selectedPaymentDate: string | undefined) => {
      const row = rawRows.find((r) => r.invoiceNumber === invoiceNumber);
      if (!row) return;

      setPendingChanges((prev) => {
        const newMap = new Map(prev);
        const existingChanges = newMap.get(row.invoiceId) || {};

        if (selectedPaymentDate) {
          newMap.set(row.invoiceId, {
            ...existingChanges,
            paymentDate: formatDateForSubmission(selectedPaymentDate),
            willPay: true,
            disputedReasonId: undefined,
            reason: undefined,
            userText: '',
          });
        } else {
          newMap.set(row.invoiceId, {
            ...existingChanges,
            paymentDate: undefined,
            willPay: false,
          });
        }

        return newMap;
      });
    },
    [rawRows],
  );

  const handleWillNotPayChange = useCallback(
    (
      invoiceNumber: string,
      disputedReasonId: string,
      disputedReasonDesc: string,
      note: string,
    ) => {
      const row = rawRows.find((r) => r.invoiceNumber === invoiceNumber);
      if (!row) return;

      setPendingChanges((prev) => {
        const newMap = new Map(prev);
        const existingChanges = newMap.get(row.invoiceId) || {};

        newMap.set(row.invoiceId, {
          ...existingChanges,
          userText: note,
          disputedReasonId: disputedReasonId,
          reason: disputedReasonDesc,
          willPay: false,
          paymentDate: undefined,
        });

        return newMap;
      });
    },
    [rawRows],
  );

  const updateRowWithModalData = useCallback(
    (
      invoiceNumber: string,
      value: string,
      disputedReasonId?: string,
      disputedReasonDesc?: string,
    ) => {
      const row = rawRows.find((r) => r.invoiceNumber === invoiceNumber);
      if (!row) return;

      setPendingChanges((prev) => {
        const newMap = new Map(prev);
        const existingChanges = newMap.get(row.invoiceId) || {};

        if (disputedReasonId) {
          newMap.set(row.invoiceId, {
            ...existingChanges,
            userText: value,
            disputedReasonId: disputedReasonId,
            reason: disputedReasonDesc,
            willPay: false,
            paymentDate: undefined,
          });
        } else {
          newMap.set(row.invoiceId, {
            ...existingChanges,
            userText: value,
          });
        }

        return newMap;
      });
    },
    [rawRows],
  );

  // Reset changes for a specific row
  const resetRowChanges = useCallback((invoiceId: number) => {
    setPendingChanges((prev) => {
      const newMap = new Map(prev);
      newMap.delete(invoiceId);
      return newMap;
    });
  }, []);

  // Reset all changes
  const resetAllChanges = useCallback(() => {
    setPendingChanges(new Map());
  }, []);

  // Check if a specific row has pending changes
  const rowHasChanges = useCallback(
    (invoiceId: number): boolean => {
      return invoiceIdsWithChanges.has(invoiceId);
    },
    [invoiceIdsWithChanges],
  );

  // Check if a specific row has a warning
  const rowHasWarning = useCallback(
    (invoiceId: number): boolean => {
      return invoiceIdsWithWarnings.has(invoiceId);
    },
    [invoiceIdsWithWarnings],
  );

  // Check if a specific row is past due
  const isRowPastDue = useCallback(
    (invoiceId: number): boolean => {
      const row = rawRows.find((r) => r.invoiceId === invoiceId);
      if (!row) return false;
      return isInvoicePastDue(row.dueDate);
    },
    [rawRows],
  );

  // Submit handler - only submits modified rows
  const handleSubmitUpdates = useCallback(async (): Promise<boolean> => {
    if (!hasChangesToSubmit) {
      console.log('No changes to submit');
      return false;
    }

    // Only validate modified rows, not all rows
    if (!modifiedRowsValidationState.isValid) {
      setSubmitError(
        `Cannot submit: ${modifiedRowsValidationState.invalidRowsCount} modified invoice(s) require a payment date or disputed reason.`,
      );
      return false;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const payload = buildSubmitPayload(modifiedRows);
      console.log('Submitting payload:', payload);

      await updateDebtorFeedback(payload);

      // Clear all pending changes after successful submission
      setPendingChanges(new Map());

      // Call success callback
      onSubmitSuccess?.();

      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to submit updates';
      setSubmitError(errorMessage);
      console.error('Failed to submit invoice updates:', error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [hasChangesToSubmit, modifiedRows, modifiedRowsValidationState, onSubmitSuccess]);

  const handleBulkPaymentDateChangeAndSubmit = useCallback(
    async (invoiceNumbers: string[], selectedPaymentDate: string, note: string = ''): Promise<boolean> => {
      const invoiceRows = invoiceNumbers
        .map((num) => rawRows.find((r) => r.invoiceNumber === num))
        .filter((row): row is InvoicesDashboard => row !== undefined);

      if (invoiceRows.length === 0) {
        setSubmitError('No valid invoices found');
        return false;
      }

      setIsSubmitting(true);
      setSubmitError(null);

      try {
        const payload = buildBulkPaymentPayload(invoiceRows, selectedPaymentDate, note);
        console.log('Submitting bulk payment payload:', payload);

        await updateDebtorFeedback(payload);

        // Clear all pending changes after successful submission
        setPendingChanges(new Map());

        onSubmitSuccess?.();

        return true;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to submit updates';
        setSubmitError(errorMessage);
        console.error('Failed to submit bulk payment updates:', error);
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [rawRows, onSubmitSuccess],
  );

  const handleBulkWillNotPayChangeAndSubmit = useCallback(
    async (
      invoiceNumbers: string[],
      disputedReasonId: string,
      disputedReasonDesc: string,
      note: string,
    ): Promise<boolean> => {
      const invoiceRows = invoiceNumbers
        .map((num) => rawRows.find((r) => r.invoiceNumber === num))
        .filter((row): row is InvoicesDashboard => row !== undefined);

      if (invoiceRows.length === 0) {
        setSubmitError('No valid invoices found');
        return false;
      }

      setIsSubmitting(true);
      setSubmitError(null);

      try {
        const payload = buildBulkWillNotPayPayload(
          invoiceRows,
          disputedReasonId,
          disputedReasonDesc,
          note
        );
        console.log('Submitting bulk will not pay payload:', payload);

        await updateDebtorFeedback(payload);

        // Clear all pending changes after successful submission
        setPendingChanges(new Map());

        onSubmitSuccess?.();

        return true;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to submit updates';
        setSubmitError(errorMessage);
        console.error('Failed to submit bulk will not pay updates:', error);
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [rawRows, onSubmitSuccess],
  );

  return {
    rows,
    pendingChanges,
    modifiedRows,
    hasChangesToSubmit,
    invoiceIdsWithChanges,
    handlePaymentDateChange,
    handleWillNotPayChange,
    updateRowWithModalData,
    handleBulkPaymentDateChangeAndSubmit,
    handleBulkWillNotPayChangeAndSubmit,
    resetRowChanges,
    resetAllChanges,
    rowHasChanges,
    handleSubmitUpdates,
    isSubmitting,
    submitError,
    isFormValid,
    // All rows validation (for display)
    hasRowsWithWarnings: allRowsValidationState.rowsWithWarningsCount > 0,
    rowsWithWarningsCount: allRowsValidationState.rowsWithWarningsCount,
    invoiceIdsWithWarnings,
    rowHasWarning,
    isRowPastDue,
    // Modified rows validation (for submission)
    hasInvalidModifiedRows: !modifiedRowsValidationState.isValid,
    invalidModifiedRowsCount: modifiedRowsValidationState.invalidRowsCount,
  };
};
